import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { TicketCategory } from "@signalist/shared-types";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { AutoCompleteItem as DropdownItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { NewTicketModal, NewTicketOverrides } from "@/components/SupportTicket/NewTicketModal";
import { useLazyGetCompanyQuery } from "@/pages/Private/redux/companies/companies.api";
import {
	useCreateStaffTicketMutation,
	useUploadStaffTicketAttachmentMutation,
} from "@/pages/Private/redux/tickets/tickets-admin.api";

import { useCompanySearch } from "../hooks/useCompanySearch";

interface PickedCompany {
	id: number;
	name: string;
}

interface StaffNewTicketModalProps {
	onClose: () => void;
	/** `customerName` for the snackbar; `failedAttachment` names a file that could not be uploaded. */
	onCreated?: (ticketId: number, customerName: string, failedAttachment?: string) => void;
}

const fullName = (user: { firstName: string; lastName: string }) =>
	`${user.firstName} ${user.lastName}`.trim();

/**
 * "New ticket for a customer" (PREC-3939 decision 7): the customer form with a company and a
 * customer picker in front of it, filing through `tickets/admin`. The customer becomes the
 * ticket's creator, the staff member its filer (PREC-3934). The staff member's own page context
 * says nothing about the customer's problem, so it is not sent.
 */
export const StaffNewTicketModal: FunctionComponent<StaffNewTicketModalProps> = ({
	onClose,
	onCreated,
}) => {
	const { t } = useTranslation();
	const companies = useCompanySearch();
	const [company, setCompany] = useState<PickedCompany | null>(null);
	const [customerId, setCustomerId] = useState<number | null>(null);
	const [showPickerError, setShowPickerError] = useState(false);

	const [loadCompany, { data: companyDetail, isFetching: isLoadingUsers }] =
		useLazyGetCompanyQuery();
	const [createStaffTicket, { isLoading: isCreating }] = useCreateStaffTicketMutation();
	const [uploadAttachment, { isLoading: isUploading }] = useUploadStaffTicketAttachmentMutation();

	const users = company && companyDetail?.id === company.id ? companyDetail.users : [];
	const customer = users.find((user) => user.id === customerId) ?? null;
	const customerOptions: DropdownItem[] = users.map((user) => ({
		id: user.id,
		title: fullName(user),
	}));

	const pickCompany = (item?: AutoCompleteItem) => {
		if (!item) {
			return;
		}

		const picked = { id: Number(item.id), name: item.title };

		setCompany(picked);
		setCustomerId(null);
		setShowPickerError(false);
		loadCompany(picked.id);
	};

	const overrides: NewTicketOverrides = {
		// Staff file the desk's own work items too, so the picker keeps the staff-only categories.
		categories: Object.values(TicketCategory),
		title: t("support.staff.modal.title"),
		intro: t("support.staff.modal.intro"),
		filedAs:
			company && customer
				? t("support.staff.modal.filedFor", { name: fullName(customer), company: company.name })
				: "",
		isBusy: isCreating || isUploading,
		canSubmit: () => {
			const ok = company !== null && customer !== null;

			setShowPickerError(!ok);

			return ok;
		},
		createTicket: (body) => {
			// The staff member's own page context says nothing about the customer's problem.
			const { pageContext: _pageContext, ...customerFields } = body;

			return createStaffTicket({
				...customerFields,
				companyId: (company as PickedCompany).id,
				onBehalfOfUserId: (customer as { id: number }).id,
			}).unwrap();
		},
		uploadAttachment: ({ ticketId, file }) => uploadAttachment({ ticketId, file }).unwrap(),
		leading: (
			<div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-status-info-light p-3">
				<AutoComplete
					data={companies.options}
					disableLocalFilter
					floating
					handleReset={() => {
						setCompany(null);
						setCustomerId(null);
					}}
					handleSearch={companies.search}
					handleSelect={pickCompany}
					isLoading={companies.isLoading}
					label={t("support.staff.modal.companyLabel")}
					placeholder={t("support.staff.modal.companyPlaceholder")}
					selectedItem={company?.name ?? ""}
				/>
				<Dropdown
					data={customerOptions}
					disabled={!company || isLoadingUsers}
					floating={true}
					handleSelect={(value: DropdownItem) => {
						setCustomerId(Number(value.id));
						setShowPickerError(false);
					}}
					label={t("support.staff.modal.customerLabel")}
					resultText={t("support.staff.modal.customerEmpty")}
					title={t("support.staff.modal.customerPlaceholder")}
					value={customerOptions.find((option) => option.id === customerId)}
				/>
				{showPickerError && (
					<p className="text-xs text-status-error">{t("support.staff.modal.pickerRequired")}</p>
				)}
			</div>
		),
	};

	return (
		<NewTicketModal
			overrides={overrides}
			onClose={onClose}
			onCreated={(ticketId, failedAttachment) =>
				onCreated?.(ticketId, customer ? fullName(customer) : "", failedAttachment)
			}
		/>
	);
};
